<template>
	<v-main data-tour-scope="warehouse-detail">
		<v-container fluid fill-height>
			<v-layout justify-center align-center>
				<v-card width="100%" outlined elevation-0>
					<v-toolbar flat class="border_bottom" shaped>
						<v-toolbar-title> {{ this.$vuetify.lang.t('$vuetify.front.warehouse.home') }} / {{ this.$vuetify.lang.t('$vuetify.front.warehouse.detail') }}</v-toolbar-title>
						<v-btn class="ml-6" outlined color="primary" data-tour="tutorial" @click="tour.start()">
							<v-icon left>mdi-school-outline</v-icon>
							Tutorial guidato
						</v-btn>

						<v-spacer></v-spacer>
					</v-toolbar>

					<v-container>
						<v-alert outlined text type="info"> Usare il lettore barcode per leggere i codici dei prodotti </v-alert>
						<v-row>
							<v-col sm="8" offset-md="2" data-tour="scan">
								<v-text-field hide-details="auto" readonly outlined append-icon="mdi-barcode" v-model="sku"></v-text-field>
								<qr-scan-panel class="mt-2 mb-2" data-tour="phone" @code="processScan" />
							</v-col>
						</v-row>
						<div v-if="sku">
							<div v-if="getElement.length === 0">
								<v-alert outlined text type="error"> Nessun prodotto trovato per questo codice</v-alert>
							</div>

							<div v-else>
								<MappaVarianti data-tour="map" :variantsList="variantsMap" :variantSetting="variantsSelectedMap" :key="componentKey" :componentKey="componentKey" />
								<!-- telefono: ogni prodotto trovato diventa una card chiave/valore -->
								<div v-if="mobileCards" class="kv-list" data-tour="table">
									<key-value-card v-for="item in getElement" :key="item.name" :title="item.trans.name" :image="getImage(item) || ''" :fields="campiCard(item)" />
								</div>
								<v-simple-table v-else data-tour="table">
									<thead>
										<tr>
											<th class="text-left">Image</th>
											<th class="text-left">Name</th>
											<th class="text-left">Variante</th>
											<th class="text-left">Qt</th>
											<!--											<th class="text-left">Qt x unità</th>-->
										</tr>
									</thead>
									<tbody>
										<tr v-for="item in getElement" :key="item.name">
											<td>
												<template v-if="getImage(item)">
													<img :src="getImage(item)" style="height: 100px" />
												</template>
											</td>
											<td>
												<span>{{ item.trans.name }}</span
												><br />
												<small>{{ item?.variants?.sku ? 'SKU: ' + item?.variants?.sku + ' - ' : '' }} {{ item?.variants?.sku ? 'EAN: ' + item?.variants?.ean : '' }}</small>
											</td>
											<td>
												<v-chip v-for="el in item.variants?.variants" v-if="item.variants?.variants?.length > 0" :key="el._id" :ripple="false" class="mr-2 mb-2" small>
													<b class="mr-2">{{ el.name.trans }}</b> {{ el.value.trans }}
												</v-chip>
											</td>
											<td>{{ item?.variants?.qt }}</td>
											<!--											<td>{{ item?.variants?.qtunit }}</td>-->
										</tr>
									</tbody>
								</v-simple-table>
							</div>
						</div>
					</v-container>
				</v-card>
			</v-layout>
		</v-container>

		<!-- Ultimo elemento: il nodo viene spostato in `[data-app]` al montaggio. -->
		<TourOverlay :tour="tour"></TourOverlay>
	</v-main>
</template>

<script>
import { mapActions, mapMutations, mapGetters } from 'vuex';
import Table from '@/components/orders/Table.vue';
import { ObjectID } from 'bson';
const MappaVarianti = () => import('@/components/items/mappavarianti.vue');
import TourOverlay from '@/components/core/tour/TourOverlay.vue';
import QrScanPanel from '@/components/warehouse/QrScanPanel.vue';
import KeyValueCard from '@/components/core/KeyValueCard.vue';
import { useTour } from '@/components/core/tour/useTour';
import { createWarehouseDetailTourSteps } from './warehouse-detail-tour';

export default {
	name: 'WarehouseDetail',
	components: { Table, MappaVarianti, TourOverlay, QrScanPanel, KeyValueCard },
	/*
	 * Il controller del tutorial nasce qui e non in `data()`: quello che sta in
	 * `data()` viene reso reattivo in profondita da Vue 2, e in quel passaggio i
	 * `ref` del motore verrebbero srotolati nel loro valore grezzo.
	 */
	setup() {
		return {
			tour: useTour(createWarehouseDetailTourSteps()),
		};
	},

	data() {
		return {
			isLoading: false,
			form: {},
			order: {},
			qty: 0,
			code: '',
			reading: false,
			snackbar: {
				snackbar: false,
				color: '',
				mode: '',
				timeout: 6000,
				text: '',
			},
			componentKey: 0,
			sku: '',
			variantsSelected: [],
			listCombines: [],
			listVariantValueExploded: [],
			variantValues: [],
			report: {},
			variantsMap: [],
			variantsSelectedMap: [],
		};
	},
	async created() {
		await this.getProfileUser();
	},
	computed: {
		...mapGetters({
			getOperator: 'packingLists/getOperator',
			getPackingList: 'packingLists/getPackingList',
			getProfile: 'profile/getId',
			getElement: 'warehouse/getItems',
			getSettings: 'settings/getSetting',
		}),
		// sotto i 769px le righe diventano card
		mobileCards() {
			return this.$vuetify.breakpoint.width < 769;
		},
	},
	methods: {
		...mapMutations({
			setPreload: 'settings/setPreload',
			setDialog: 'settings/setDialog',
		}),
		...mapActions({
			getItems: 'warehouse/getDetail',
			destroyItems: 'warehouse/destroyData',
			getProfileUser: 'profile/getProfile',
			editItem: 'packingLists/edit',
			updateItem: 'warehouse/updateItem',
			resetItem: 'packingLists/reset',
			createBarcodeOrder: 'barcode/createBarcodeOrder',
			getDetailItem: 'items/getDetailItem',
		}),
		close() {
			this.setDialog(false);
			this.$emit('message', 'refresh');
		},
		// coppie chiave/valore della card del prodotto letto (vista telefono)
		campiCard(item) {
			const variante = (item?.variants?.variants || []).map((el) => `${el.name?.trans}: ${el.value?.trans}`).join(', ');
			return [
				{ label: 'SKU', value: item?.variants?.sku },
				{ label: 'EAN', value: item?.variants?.ean },
				{ label: 'Variante', value: variante },
				{ label: 'Qt', value: item?.variants?.qt, strong: true },
			];
		},
		getImage(item) {
			let variant_id = item.variants?._id;
			if (variant_id) {
				let variant_ids = item.variants?.variants.map(item => item._id);
				let image = item.images.find(el => el.variants.includes(variant_id) || variant_ids.filter(item => el.variants.includes(item)).length > 0);
				if (image) {
					return image.sizes[0]?.value;
				} else {
					return false;
				}
			} else {
				return false;
			}
		},

		async update() {
			await this.setPreload(true);
			var result = await this.updateItem({ id: this.form._id, item: this.form });
			this.form = JSON.parse(JSON.stringify(result.result.result));
			this.form.history.sort(function (a, b) {
				return b.date - a.date;
			});
			await this.setPreload(false);
		},

		async onKeypress(event) {
			if (event.keyCode === 13) {
				await this.processScan(this.code);
			} else {
				this.sku = '';
				this.code += event.key;
			}
		},

		// Unico punto di ingresso dei codici letti: pistola USB (Invio) e telefono (QrScanPanel).
		async processScan(code) {
			this.code = code;
			//this.$forceUpdate();

			this.variantsMap = [];
			this.variantsSelectedMap = [];
			await this.setPreload(true);
			await this.getItems({ search: this.code });
			this.listCombines = [];
			this.variantValues = [];
			this.listVariantValueExploded = [];
			this.variantsSelected = [];
			//console.log('this.getSettings.variants', this.getSettings.variants);
			for await (let el of this.getElement) {
				for await (let value of el.variants.variants) {
					//_.forEach(data.data.data.variants, el => {
					//console.log('value', value);
					//console.log('el.variants._id.toString()', value.name._id.toString());
					let variant = _.find(this.getSettings.variants, { _id: value.name._id.toString() });
					//console.log('variant', variant);
					if (variant) {
						if (!_.includes(this.variantValues, `${value.name.trans}#${value.value.trans}`)) {
							this.variantValues.push(`${value.name.trans}#${value.value.trans}`);
							let check = _.find(this.listVariantValueExploded, { name: value.name.trans, value: value.value.trans });
							if (!check) {
								this.listVariantValueExploded = [
									{
										_id: value._id,
										name: value.name._id,
										value: value.value._id,
										text: `${value.name.trans}: ${value.name.trans}`,
									},
									...this.listVariantValueExploded,
								];
							}
						}
						if (!_.some(this.listCombines, { _id: variant._id })) {
							this.listCombines.push(variant);
							this.variantsSelected.push(variant._id);
						}
					}
				}
			}

			/*console.log('this.variantsSelected', this.variantsSelected);
			console.log('this.listCombines', this.listCombines);
			console.log('this.getElement._id', this.getElement);*/
			this.report = await this.getDetailItem({ id: this.getElement[0]._id });
			this.variantsMap = [...this.report.data.data.variants];
			this.variantsSelectedMap = [...this.variantsSelected];

			this.sku = this.code;
			this.code = '';

			//console.log('this.variantsMap', this.variantsMap);
			//console.log('this.variantsSelectedMap', this.variantsSelectedMap);
			this.componentKey = Date.now();
			await this.setPreload(false);
		},
	},
	mounted() {
		document.addEventListener('keypress', this.onKeypress);
	},
	beforeDestroy() {
		// Se si lascia la pagina a giro aperto, vanno staccati i listener del tutorial.
		this.tour.stop();
		document.removeEventListener('keypress', this.onKeypress);
	},
};
</script>

<style scoped>
/* card in lista fuori da v-data-table */
.kv-list > .kv-card + .kv-card {
	margin-top: 8px;
}
</style>
